import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { cartApi, orderApi } from '../services/api'
import { useAuth } from '../hooks/useAuth'
import Button from '../components/common/Button'

/**
 * Cart — view, edit quantities, remove items.
 *
 * Each line has its own quantity input that updates onBlur (or on Enter).
 * Optimistic-ish: we re-fetch the whole cart after every mutation so
 * stock levels and totals always reflect what the server thinks.
 *
 * Checkout button is here but disabled — comes alive once we build the
 * checkout flow.
 */
export default function Cart() {
    const { user } = useAuth()
    const noCustomer = !user?.customerId

    const [cart, setCart] = useState(null)
    const [loading, setLoading] = useState(true)
    const [busyLineId, setBusyLineId] = useState(null)
    const [clearing, setClearing] = useState(false)

    const [drafts, setDrafts] = useState({})

    const [preview, setPreview] = useState(null)
    const [notes, setNotes] = useState('')
    const [checkingOut, setCheckingOut] = useState(false)
    const navigate = useNavigate()

    useEffect(() => { load() }, [])

    async function loadPreview() {
        try {
            const response = await orderApi.preview()
            if (response.success) setPreview(response.preview)
        } catch { /* non-fatal */ }
    }

    async function load() {
        setLoading(true)
        try {
            const response = await cartApi.get()
            if (response.success) {
                setCart(response.cart)
                // Reset drafts to match server state.
                const draftMap = {}
                for (const item of response.cart.items) {
                    draftMap[item.id] = String(item.quantity)
                }
                setDrafts(draftMap)
                // Refresh preview alongside (cycle + backorder split)
                loadPreview()
            } else {
                toast.error(response.message || 'Could not load cart')
            }
        } catch {
            toast.error('Network error loading cart')
        } finally {
            setLoading(false)
        }
    }

    async function commitQuantity(line) {
        const raw = drafts[line.id] ?? String(line.quantity)
        const n = parseInt(raw, 10)

        if (isNaN(n) || n < 0) {
            toast.error('Quantity must be a non-negative number')
            // Reset draft to current server value.
            setDrafts(d => ({ ...d, [line.id]: String(line.quantity) }))
            return
        }

        if (n === line.quantity) return  // no change

        setBusyLineId(line.id)
        try {
            const response = await cartApi.update(line.id, n)
            if (response.success) {
                if (n === 0) toast.success('Item removed')
                await load()
                window.dispatchEvent(new Event('ghasc:cart-changed'))
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusyLineId(null)
        }
    }


    async function stepQuantity(line, delta) {
        const newQty = Math.max(0, line.quantity + delta)
        if (newQty === line.quantity) return

        setBusyLineId(line.id)
        try {
            const response = await cartApi.update(line.id, newQty)
            if (response.success) {
                if (newQty === 0) toast.success('Item removed')
                await load()
                window.dispatchEvent(new Event('ghasc:cart-changed'))
            } else {
                toast.error(response.message || 'Update failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusyLineId(null)
        }
    }

    async function handleRemove(line) {
        if (!confirm(`Remove "${line.description}" from your cart?`)) return
        setBusyLineId(line.id)
        try {
            const response = await cartApi.remove(line.id)
            if (response.success) {
                toast.success('Item removed')
                await load()
                window.dispatchEvent(new Event('ghasc:cart-changed'))
            } else {
                toast.error(response.message || 'Remove failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setBusyLineId(null)
        }
    }

    async function handleClear() {
        if (!confirm('Empty your entire cart? This can\'t be undone.')) return
        setClearing(true)
        try {
            const response = await cartApi.clear()
            if (response.success) {
                toast.success('Cart emptied')
                await load()
                window.dispatchEvent(new Event('ghasc:cart-changed'))
            } else {
                toast.error(response.message || 'Could not clear cart')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setClearing(false)
        }
    }

    async function handleCheckout() {
        if (!cart || cart.items.length === 0) return

        // Confirm if there are backorders
        if (preview?.backorderCount > 0) {
            const ok = confirm(
                `${preview.backorderCount} of your ${preview.lineCount} item${preview.lineCount === 1 ? '' : 's'} ` +
                `will be backordered (we're out of stock). The rest will be ready for ` +
                `pickup. Continue with checkout?`
            )
            if (!ok) return
        } else {
            const ok = confirm('Submit this order? You\'ll receive a confirmation email with payment details.')
            if (!ok) return
        }

        setCheckingOut(true)
        try {
            const response = await orderApi.checkout({ notes: notes.trim() || null })
            if (response.success) {
                toast.success('Order submitted!')
                window.dispatchEvent(new Event('ghasc:cart-changed'))
                // Navigate to success page with order id
                navigate(`/checkout/success?id=${response.order.id}`)
            } else {
                toast.error(response.message || 'Checkout failed')
            }
        } catch {
            toast.error('Network error')
        } finally {
            setCheckingOut(false)
        }
    }

    // ----- No-customer state -------------------------------------------------
    if (noCustomer) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Account not linked yet</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    Your account isn't linked to a homegroup or subcommittee yet, so you
                    can't have a cart. Reach out to the literature chair to be linked.
                </p>
            </div>
        )
    }

    if (loading) {
        return <div className="p-8 text-center text-gray-500">Loading cart...</div>
    }

    if (!cart || cart.items.length === 0) {
        return (
            <div className="bg-white rounded-xl shadow-sm p-8 text-center max-w-xl mx-auto">
                <h2 className="text-xl font-semibold text-ghasc-navy">Your cart is empty</h2>
                <p className="text-gray-600 mt-2 text-sm">
                    {cart?.customerName
                        ? `${cart.customerName} hasn't added anything yet.`
                        : 'Nothing in your cart yet.'}
                </p>
                <div className="mt-4">
                    <Link to="/shop">
                        <Button>Browse the catalog</Button>
                    </Link>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                    <h2 className="text-2xl font-semibold text-ghasc-navy">Cart</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        {cart.customerName ? `Cart for ${cart.customerName}.` : null}{' '}
                        All members of your customer share the same cart.
                    </p>
                </div>
                <div className="flex gap-2">
                    <Link to="/shop">
                        <Button variant="secondary">Continue Shopping</Button>
                    </Link>
                    <Button variant="danger" onClick={handleClear} loading={clearing}>
                        Empty Cart
                    </Button>
                </div>
            </div>

            {/* Line items — card per item */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {cart.items.map((line) => {
                    const variantLabel = [line.year, line.color].filter(Boolean).join(' / ')
                    const stockShort = line.inventoryQuantity < line.quantity
                    const draftQty = drafts[line.id] ?? line.quantity
                    const isBusy = busyLineId === line.id

                    return (
                        <div key={line.id} className="bg-white rounded-xl shadow-sm p-4 flex flex-col gap-3">
                            {/* Header row: image + description */}
                            <div className="flex gap-3">
                                {line.imageUrl ? (
                                    <img src={line.imageUrl} alt=""
                                        className="w-16 h-16 object-contain bg-gray-50 rounded flex-shrink-0" />
                                ) : (
                                    <div className="w-16 h-16 bg-gray-100 rounded text-xs text-gray-400 flex items-center justify-center flex-shrink-0">
                                        —
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 break-words">
                                        {line.description}
                                    </div>
                                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                                        {line.itemNumber}
                                    </div>
                                    {variantLabel && (
                                        <div className="text-xs text-gray-500 mt-0.5">{variantLabel}</div>
                                    )}
                                    <div className="text-xs text-gray-600 mt-1">
                                        ${Number(line.cost).toFixed(2)} each
                                    </div>
                                </div>
                            </div>

                            {/* Backorder warning */}
                            {stockShort && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2 text-xs text-amber-800">
                                    Only {line.inventoryQuantity} in stock — extras will be backordered.
                                </div>
                            )}

                            {/* Quantity stepper — big tap targets for mobile */}
                            <div className="flex items-center justify-between gap-2">
                                <div className="text-xs uppercase tracking-wider text-gray-500">Quantity</div>
                                <div className="flex items-center gap-1">
                                    <button
                                        type="button"
                                        onClick={() => stepQuantity(line, -1)}
                                        disabled={isBusy || line.quantity <= 0}
                                        className="no-spinner w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-lg font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-30"
                                        aria-label="Decrease quantity"
                                    >
                                        −
                                    </button>
                                    <input
                                        type="number"
                                        inputMode="numeric"
                                        min="0"
                                        value={draftQty}
                                        onChange={(e) => setDrafts(d => ({ ...d, [line.id]: e.target.value }))}
                                        onBlur={() => commitQuantity(line)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur() }}
                                        disabled={isBusy}
                                        className="no-spinner w-14 h-10 text-center border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => stepQuantity(line, 1)}
                                        disabled={isBusy}
                                        className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg text-lg font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-30"
                                        aria-label="Increase quantity"
                                    >
                                        +
                                    </button>
                                </div>
                            </div>

                            {/* Subtotal + remove */}
                            <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                                <div>
                                    <div className="text-xs uppercase tracking-wider text-gray-500">Subtotal</div>
                                    <div className="font-semibold text-ghasc-navy">
                                        ${Number(line.subtotal).toFixed(2)}
                                    </div>
                                </div>
                                <button
                                    onClick={() => handleRemove(line)}
                                    disabled={isBusy}
                                    className="px-3 py-2 text-sm text-ghasc-danger bg-red-50 rounded-lg hover:bg-red-100 disabled:opacity-50"
                                >
                                    Remove
                                </button>
                            </div>

                            {/* Added by — small footnote */}
                            {line.addedByUserName && (
                                <div className="text-xs text-gray-400 -mt-1">
                                    Added by {line.addedByUserName}
                                </div>
                            )}
                        </div>
                    )
                })}
            </div>

            {/* Cart totals summary */}
            <div className="bg-gray-50 rounded-xl p-4 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm text-gray-600">
                    {cart.lineCount} line{cart.lineCount === 1 ? '' : 's'}
                    {' · '}
                    {cart.totalQuantity} item{cart.totalQuantity === 1 ? '' : 's'}
                </div>
                <div className="text-right">
                    <div className="text-xs uppercase tracking-wider text-gray-500">Total</div>
                    <div className="text-xl font-semibold text-ghasc-navy">
                        ${Number(cart.total).toFixed(2)}
                    </div>
                </div>
            </div>

            {/* Cycle banner — what cycle this order falls under */}
            {preview && (
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-900">
                    📅 This order will be filled for the{' '}
                    <span className="font-semibold">{preview.cycleDisplay}</span> meeting.{' '}
                    Pickup on <span className="font-semibold">
                        {new Date(preview.pickupDate).toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric',
                        })}
                    </span>. Orders must be in by{' '}
                    <span className="font-semibold">
                        {new Date(preview.cutoff).toLocaleDateString('en-US', {
                            weekday: 'long', month: 'long', day: 'numeric',
                        })}, {new Date(preview.cutoff).toLocaleTimeString('en-US', {
                            hour: 'numeric', minute: '2-digit', hour12: true,
                        })}
                    </span>.
                </div>
            )}

            {/* Backorder warning */}
            {preview?.backorderCount > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
                    <div className="font-semibold mb-1">⚠ Some items will be backordered</div>
                    <p>
                        {preview.backorderCount} of your {preview.lineCount} item{preview.lineCount === 1 ? '' : 's'} are
                        currently out of stock. The chair will fill the available portion now, and the
                        rest when the next regional shipment arrives.
                    </p>
                </div>
            )}

            {/* Checkout */}
            <div className="bg-white rounded-xl shadow-sm p-6 space-y-4">
                <div>
                    <h3 className="text-lg font-semibold text-ghasc-navy">Ready to order?</h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Submitting will lock in this cart and email you a confirmation with payment details.
                    </p>
                </div>

                <div>
                    <label htmlFor="cart-notes" className="block text-sm font-medium text-gray-700 mb-1">
                        Order notes (optional)
                    </label>
                    <textarea
                        id="cart-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={2}
                        placeholder="Anything the chair should know — e.g. 'will be picked up by Sarah'"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-ghasc-navy"
                    />
                </div>

                <div className="flex justify-end">
                    <Button onClick={handleCheckout} loading={checkingOut}>
                        Submit Order — ${Number(cart.total).toFixed(2)}
                    </Button>
                </div>
            </div>
        </div>
    )
}